<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between">
        <h2 class="text-xl font-bold">Status Master</h2>
        <button @click="openForm" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          + New Status
        </button>
      </div>

      <div v-if="masterDataStore.loading" class="text-center py-12">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="masterDataStore.statuses.length === 0" class="text-center py-12 text-gray-500">
        No statuses found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">ID</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Code</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Category</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Description</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="status in masterDataStore.statuses" :key="status.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm text-gray-500">{{ status.id }}</td>
              <td class="px-6 py-4 text-sm font-mono">{{ status.code }}</td>
              <td class="px-6 py-4 text-sm font-medium">{{ status.name }}</td>
              <td class="px-6 py-4 text-sm capitalize">{{ status.category }}</td>
              <td class="px-6 py-4 text-sm">{{ status.description || '-' }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(status.is_active) === '1' || status.is_active === true ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(status.is_active) === '1' || status.is_active === true ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editStatus(status)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteStatusFunc(status.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-50 relative">
        <h3 class="text-lg font-bold mb-4">{{ editingStatus ? 'Edit Status' : 'New Status' }}</h3>
        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Code *</label>
            <input v-model="formData.code" required placeholder="e.g., surgery_pending" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Name *</label>
            <input v-model="formData.name" required placeholder="e.g., Surgery Pending" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Category *</label>
            <input v-model="formData.category" required placeholder="e.g., catching, fitness, surgery" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Description</label>
            <input v-model="formData.description" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" />
          </div>
          <label class="flex items-center gap-2 text-sm">
            <input v-model="formData.is_active" type="checkbox" class="w-4 h-4" />
            Active
          </label>
          <div class="flex justify-end gap-2 pt-4 border-t">
            <button type="button" @click="closeForm" class="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              {{ editingStatus ? 'Update' : 'Create' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingStatus = ref(null)
const formData = ref({ code: '', name: '', category: '', description: '', is_active: true })

const openForm = () => {
  showForm.value = true
}

const closeForm = () => {
  showForm.value = false
  editingStatus.value = null
  formData.value = { code: '', name: '', category: '', description: '', is_active: true }
}

const editStatus = (status) => {
  editingStatus.value = status
  formData.value = {
    code: status.code,
    name: status.name,
    category: status.category,
    description: status.description || '',
    is_active: String(status.is_active) === '1' || status.is_active === true,
  }
  showForm.value = true
}

const submitForm = async () => {
  try {
    const data = {
      code: formData.value.code.trim().toLowerCase().replace(/\s+/g, '_'),
      name: formData.value.name.trim(),
      category: formData.value.category.trim().toLowerCase().replace(/\s+/g, '_'),
      description: formData.value.description || '',
      is_active: formData.value.is_active ? 1 : 0,
    }

    if (editingStatus.value) {
      await masterDataStore.updateStatus(editingStatus.value.id, data)
      Swal.fire({ icon: 'success', title: 'Success', text: 'Status updated successfully', confirmButtonColor: '#3085d6' })
    } else {
      await masterDataStore.createStatus(data)
      Swal.fire({ icon: 'success', title: 'Success', text: 'Status created successfully', confirmButtonColor: '#3085d6' })
    }

    closeForm()
    await masterDataStore.getStatuses()
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || masterDataStore.error || 'Failed to save status', confirmButtonColor: '#3085d6' })
  }
}

const deleteStatusFunc = async (id) => {
  const result = await Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this status?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel',
  })

  if (!result.isConfirmed) return

  try {
    await masterDataStore.deleteStatus(id)
    Swal.fire({ icon: 'success', title: 'Deleted', text: 'Status deleted successfully', confirmButtonColor: '#3085d6' })
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || masterDataStore.error || 'Failed to delete status', confirmButtonColor: '#3085d6' })
  }
}

onMounted(async () => {
  await masterDataStore.getStatuses()
})
</script>
